import React from 'react'
import PropTypes from 'prop-types'
import { Button, FlowGrid, Row } from '@fs/zion-ui'
import { ArrowCaret } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'

/**
 * CardFooter
 *
 * A shared footer component for card forms that handles two primary states:
 * 1. reviewed: Shows Cancel + Save buttons (edit mode after initial save)
 * 2. not reviewed: Shows Next or Skip button (first-time flow)
 *
 * All button labels are translated internally using common translation keys shared across all cards.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.reviewed - Whether the card has been reviewed (controls footer layout).
 * @param {Function} [props.onCancel] - Handler to cancel edits. Only used when reviewed=true.
 * @param {boolean} [props.saving=false] - When true, disables buttons and shows "Saving..." text.
 * @param {boolean} [props.isButtonDisabled=false] - When true, disables the primary action button.
 * @param {boolean} [props.hasData=true] - Whether the form has data. When false and not reviewed, shows Skip instead of Next.
 * @param {boolean} [props.showNextIcon=true] - Whether to show the arrow icon on Next/Skip buttons.
 * @param {string} [props.label='card'] - Label used for both data-testid and linkName (e.g., "events" → testid: "card-events-next-button", linkName: "events-next").
 * @param {string} [props.customLinkNamePrefix] - Custom prefix for linkName (e.g., "Additional Facts Footer" → "Additional Facts Footer Next"). When provided, overrides label for linkName only.
 * @returns {JSX.Element} The footer with appropriate buttons based on state.
 */
const CardFooter = ({
  reviewed,
  onCancel = () => {},
  saving = false,
  isButtonDisabled = false,
  hasData = true,
  showNextIcon = true,
  label = 'card',
  customLinkNamePrefix = null,
}) => {
  const [t] = useTranslation()

  // Helper to generate linkName in the correct format
  const getLinkName = (action) => {
    // If custom prefix is provided, use title case action with space separator
    if (customLinkNamePrefix) {
      const titleCaseAction = action.charAt(0).toUpperCase() + action.slice(1)
      return `${customLinkNamePrefix} ${titleCaseAction}`
    }
    // Standard format: label-action (e.g., "events-next", "family-members-save")
    return `${label}-${action}`
  }

  // Reviewed state: Show Cancel + Save buttons
  if (reviewed) {
    return (
      <FlowGrid gutters="xxs" columnCount={2} orientation="horizontal">
        <Button
          emphasis="medium"
          onClick={onCancel}
          fullWidth
          data-testid={`card-${label}-cancel-button`}
          linkName={getLinkName('cancel')}
          disabled={saving}
        >
          {t('navigation.cancel-button', 'Cancel')}
        </Button>
        <Button
          type="submit"
          emphasis="high"
          fullWidth
          disabled={isButtonDisabled || saving}
          data-testid={`card-${label}-save-button`}
          linkName={getLinkName('save')}
        >
          {saving ? t('navigation.saving', 'Saving...') : t('navigation.save-button', 'Save')}
        </Button>
      </FlowGrid>
    )
  }

  // Not reviewed + no data: Show Skip button
  if (!hasData) {
    return (
      <Button
        type="submit"
        emphasis="high"
        fullWidth
        disabled={saving}
        data-testid={`card-${label}-skip-button`}
        linkName={getLinkName('skip')}
        Icon={showNextIcon ? ArrowCaret : undefined}
        iconDirection={showNextIcon ? 'down' : undefined}
        iconAtEnd={showNextIcon}
      >
        {t('navigation.skip-button', 'Skip')}
      </Button>
    )
  }

  // Not reviewed + has data: Show Next button
  return (
    <Row guttersX="xs">
      <Button
        type="submit"
        emphasis="high"
        fullWidth
        growX
        disabled={isButtonDisabled || saving}
        data-testid={`card-${label}-next-button`}
        linkName={getLinkName('next')}
        Icon={showNextIcon ? ArrowCaret : undefined}
        iconDirection={showNextIcon ? 'down' : undefined}
        iconAtEnd={showNextIcon}
      >
        {saving ? t('navigation.saving', 'Saving...') : t('navigation.next-button', 'Next')}
      </Button>
    </Row>
  )
}

CardFooter.propTypes = {
  reviewed: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  saving: PropTypes.bool,
  isButtonDisabled: PropTypes.bool,
  hasData: PropTypes.bool,
  showNextIcon: PropTypes.bool,
  label: PropTypes.string,
  customLinkNamePrefix: PropTypes.string,
}

export default CardFooter
